// Coste interno del viaje (Logística 2, nivel 1), siempre visible dentro de la
// tarjeta "Lugar del evento", bajo la ruta: tiempo de la persona (ida y vuelta
// a su tarifa, o sin coste si es socio), combustible al precio del día (o el
// fijado al confirmar), desgaste de cada furgoneta (solo km × €/km: el valor
// de compra y la vida útil van en la ficha de la furgoneta) y total del viaje. Los
// importes de combustible y desgaste vienen del backend
// (estadoReglas.transporte.combustible); aquí solo se escriben.
import React from 'react';
import { formatEuros, numeroEs } from '../../../utils/logistica2';
import { avisosPresupuesto } from '../DetalleCombustible';

const claseFila = 'flex items-baseline justify-between gap-3 py-1';

// "Vito (propiedad): 63,6 km × 0,17 €/km = 10,81 €" -> ["Vito (propiedad)", "63,6 km × 0,17 €/km"]
const partesDesgaste = (texto = '') => {
  const i = texto.indexOf(': ');
  const etiqueta = i >= 0 ? texto.slice(0, i) : texto;
  const resto = i >= 0 ? texto.slice(i + 2) : '';
  const j = resto.lastIndexOf(' = ');
  return [etiqueta, j >= 0 ? resto.slice(0, j) : resto];
};

const Fila = ({ titulo, detalle, importe, testid, fuerte = false }) => (
  <div className={claseFila} data-testid={testid}>
    <span className="min-w-0">
      <span className={fuerte ? 'font-semibold text-[#16221D]' : 'text-[#33443C]'}>{titulo}</span>
      {detalle && <span className="block text-xs md:text-[11px] text-[#5B6B64]">{detalle}</span>}
    </span>
    <span className={`shrink-0 tabular-nums ${fuerte ? 'font-bold text-[#16221D]' : 'text-[#33443C]'}`}>
      {importe == null ? '...' : formatEuros(importe)}
    </span>
  </div>
);

// Total del viaje: tiempo de la persona + combustible y desgaste
export const totalViaje = (ruta, transporte) => {
  if (!ruta) return null;
  const c = transporte?.combustible || null;
  const combustible = c && c.total != null ? Number(c.total) || 0 : ruta.gasoil;
  if (combustible == null) return ruta.total;
  return Math.round(((ruta.personal || 0) + combustible) * 100) / 100;
};

const CosteViaje = ({ ruta, transporte }) => {
  if (!ruta) return null;
  const c = transporte?.combustible || null;
  const total = totalViaje(ruta, transporte);
  const textoComb = String(c?.texto_combustible || '').replace(/^Combustible:\s*/, '');
  let notaPrecio = c?.nota || '';
  if (!notaPrecio && c) notaPrecio = c.congelado ? 'Precio fijado al confirmar' : 'Se fija al confirmar';

  return (
    <div className="rounded-lg bg-[#F7F9F8] px-3 py-2 text-xs" data-testid="l2-coste-viaje">
      <div className="flex flex-wrap items-center gap-2 pb-1 border-b border-[#E1E6E3]">
        <span className="inline-flex px-1.5 py-px rounded-full text-xs md:text-[11px] font-bold bg-[#EEF1EF] text-[#4A5A53]">coste interno</span>
        <span className="text-[#5B6B64]">Viaje de ida y vuelta: {numeroEs(ruta.kmIdaVuelta, 1)} km</span>
      </div>
      <div className="divide-y divide-[#EEF1EF]">
        <Fila
          titulo="Tiempo de la persona"
          detalle={ruta.sinCoste ? `${ruta.minutosIdaVuelta} min, sin coste: socio` : ruta.detallePersonal}
          importe={ruta.personal}
          testid="l2-coste-viaje-persona"
        />
        {c && !c.sin_dato ? (
          <>
            <Fila
              titulo="Combustible"
              detalle={[textoComb, notaPrecio].filter(Boolean).join(' · ')}
              importe={c.importe}
              testid="l2-coste-viaje-combustible"
            />
            {(c.desgastes || []).map((d, i) => {
              const [etiqueta, formula] = partesDesgaste(d.texto);
              return (
                <Fila key={`${d.nombre}-${i}`} titulo={`Desgaste ${etiqueta}`} detalle={formula} importe={d.importe}
                  testid="l2-coste-viaje-desgaste" />
              );
            })}
          </>
        ) : (
          <Fila
            titulo="Combustible y desgaste"
            detalle={[c?.texto_combustible ? textoComb : null, c?.nota || null].filter(Boolean).join(' · ') || null}
            importe={c ? c.total : ruta.gasoil}
            testid="l2-coste-viaje-combustible"
          />
        )}
        <Fila titulo="Total del viaje" importe={total} testid="l2-coste-viaje-total" fuerte />
      </div>
      {avisosPresupuesto(c?.avisos).map(a => (
        <p key={a} className="mt-1 rounded-md px-2 py-1 bg-[#FDF0DC] text-[#7A3A06]" data-testid="l2-coste-viaje-aviso">{a}</p>
      ))}
    </div>
  );
};

export default CosteViaje;
